'use client'

import { type MouseEvent, type ReactNode, useCallback, useRef, useState } from 'react'

import { Anchor } from '@/components/client/seo/anchor'
import { useAnalytics } from '@/hooks/client'
import { EventName } from '@/lib/analytics/events'
import { utmize } from '@/lib/analytics/utmize'

export interface CouponCalloutCoupon {
  code?: string | null
  message?: string | null
  url?: string | null
  amountOff?: number | null
  percentOff?: number | null
}

export interface CouponCalloutText {
  autoApplied: string
  close: string
  copied: string
  copy: string
  from: string
  getDeal: string
  useCode: string
  visitStore: string
}

/** Identifies the surface a coupon modal was opened from. The modal's two
 *  actions report as `<position>-popup` (the outbound CTA) and `<position>-copy`
 *  (the code copy), mirroring the old app's `*-coupon-popup` naming. */
export interface CouponCalloutTracking {
  position: string
  /** Release/product the coupon belongs to — the `data-event-product` value. */
  product: string
  seller?: string | null
  sellerType?: string | null
}

interface CouponCalloutProps {
  coupon: CouponCalloutCoupon
  affiliateUrl?: string
  label: string
  text: CouponCalloutText
  /** Shown as a "From {seller}" subtitle under the modal title, when provided. */
  sellerName?: string | null
  /** Omit to leave the modal's actions untracked (e.g. a preview surface). */
  tracking?: CouponCalloutTracking
  /** Custom trigger content — defaults to tag icon + computed label */
  children?: ReactNode
  /** CSS classes for the trigger button */
  className?: string
}

type TrackingAttrs = Record<string, string | boolean>

/** The modal's outbound click. Emits the same AffiliateAction the page's other
 *  CTAs emit, so coupon-modal clicks land in the affiliate funnel alongside them. */
const ctaTrackingAttrs = (tracking: CouponCalloutTracking | undefined, label: string, url: string): TrackingAttrs => {
  if (!tracking) {
    return {}
  }
  const position = `${tracking.position}-popup`
  return {
    'data-track': true,
    'data-event-name': EventName.AffiliateAction,
    'data-event-position': position,
    'data-event-product': tracking.product,
    'data-event-seller': tracking.seller ?? '',
    'data-affiliate-seller-type': tracking.sellerType ?? 'affiliate',
    'data-affiliate-cta': label,
    'data-affiliate-text': label,
    'data-affiliate-test-name': position,
    'data-event-url': url,
  }
}

/** Copying the code is intent, not a click-through — its own event so it never
 *  inflates the affiliate click count. */
const copyTrackingAttrs = (tracking: CouponCalloutTracking | undefined, code: string): TrackingAttrs => {
  if (!tracking) {
    return {}
  }
  return {
    'data-track': true,
    'data-event-name': EventName.CouponCopy,
    'data-event-position': `${tracking.position}-copy`,
    'data-event-product': tracking.product,
    'data-event-seller': tracking.seller ?? '',
    'data-event-coupon': code,
  }
}

const TagIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5 flex-shrink-0"
    aria-hidden="true"
  >
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  </svg>
)

const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)

export const CouponCallout = ({
  coupon,
  affiliateUrl,
  label,
  text,
  sellerName,
  tracking,
  children,
  className,
}: CouponCalloutProps) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const { segment } = useAnalytics()

  const handleCopy = useCallback(() => {
    if (!coupon.code) {
      return
    }
    navigator.clipboard.writeText(coupon.code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    })
  }, [coupon.code])

  // Close on backdrop clicks. Deliberately NOT `stopPropagation()` on the modal
  // content: the analytics engine listens for [data-track] clicks at the
  // document, and a stopped native event never reaches it — the CTA and copy
  // button below would go untracked.
  const handleOverlayClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!contentRef.current?.contains(e.target as Node)) {
      setOpen(false)
    }
  }, [])

  // A coupon with a code has to be typed in at checkout; one without is a plain
  // deal link the store applies on arrival. That's the only distinction the modal
  // needs — the `code` field decides it, so nothing else has to be configured.
  //
  // `affiliateUrl` arrives pre-utmized from the server surface, but `coupon.url`
  // is the raw DB value — tag it here. The `window` guard only skips SSR, where
  // the modal (open=false) never renders, so no untagged href can reach the DOM.
  const ctaUrl =
    coupon.url && typeof window !== 'undefined'
      ? utmize({
          url: coupon.url,
          content: tracking ? `${tracking.position}-popup` : 'coupon-popup',
          release: tracking?.product,
          pathname: window.location.pathname,
          search: window.location.search,
          segment,
        })
      : coupon.url || affiliateUrl
  const ctaLabel = coupon.code ? text.visitStore : text.getDeal

  return (
    <>
      <button
        type="button"
        className={
          className ||
          'text-error inline-flex cursor-pointer items-center gap-1 text-sm font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid'
        }
        onClick={() => setOpen(true)}
        data-tracking="ignore"
      >
        {children || (
          <>
            <TagIcon />
            {label}
          </>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center" onClick={handleOverlayClick}>
          <div className="bg-overlay fixed inset-0" />
          <div ref={contentRef} className="relative z-10 mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            {/* Close button */}
            <button
              type="button"
              className="absolute top-4 right-4 cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:text-slate-600"
              onClick={() => setOpen(false)}
              aria-label={text.close}
              data-tracking="ignore"
            >
              <XIcon />
            </button>

            {/* Title */}
            <h3 className="text-navy pr-8 text-lg font-bold">{label}</h3>
            {sellerName && (
              <p className="mt-1 text-sm text-slate-500">
                {text.from} {sellerName}
              </p>
            )}

            {/* Content */}
            <div className="mt-5">
              {coupon.code ? (
                <div className="mb-5">
                  <p className="mb-2 text-xs font-medium text-slate-500">{text.useCode}</p>
                  <div className="flex items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="flex-1 font-mono text-lg font-bold tracking-wider text-slate-800">
                      {coupon.code}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={
                        copied
                          ? 'cursor-pointer rounded-lg bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors'
                          : 'bg-navy hover:bg-navy/90 cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold text-white transition-colors'
                      }
                      {...copyTrackingAttrs(tracking, coupon.code)}
                    >
                      {copied ? text.copied : text.copy}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-5 rounded-xl bg-emerald-50 px-4 py-3">
                  <p className="text-sm font-medium text-emerald-800">{text.autoApplied}</p>
                </div>
              )}

              {/* CTA */}
              {ctaUrl && (
                <Anchor
                  href={ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="bg-navy hover:bg-navy/90 inline-flex w-full items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold text-white transition-colors"
                  {...ctaTrackingAttrs(tracking, ctaLabel, ctaUrl)}
                >
                  {ctaLabel}
                  <ExternalLinkIcon />
                </Anchor>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
